/**
 * F2.1 — die Freigabeliste: was hinaus WILL, aber noch nicht darf.
 *
 * WARUM ES DIESE SEITE GIBT: Antoines Invariante vom 11.08.2026, wörtlich *„MAESTRO darf KEINE
 * Mails etc. ohne Freigabe verschicken"*. F0.1 hat den automatischen Versand gesperrt, F1 die
 * Warteschlange, die Zustandsübergänge, den einen Versandweg und die Routen gebaut. Damit steht
 * die ganze Kette — und **niemand kommt an sie heran**: der Betreiber müsste `GET /api/ausgaenge`
 * von Hand aufrufen. Ohne diese Seite gibt der Sweep Vorschläge ab, die nie jemand liest, und
 * kein Kunde bekommt je wieder eine Erinnerung.
 *
 * Seit F2.2 lässt sich jede Zeile aufklappen und zeigt den vollen Wortlaut (siehe `Wortlaut`
 * weiter unten), seit F2.3 tragen die Zeilen die beiden Knöpfe und die Sammelfreigabe
 * (siehe `Entscheidung` weiter unten).
 *
 * ## Die Gesamtzahl ist kein Schmuck
 *
 * `GET /api/ausgaenge` deckelt eine Seite auf 200 Zeilen (der Wortlaut jeder Mail hängt mit dran)
 * und liefert deshalb `gesamt` daneben. Zeigte die Seite nur die geladenen Zeilen, sähe sie bei
 * 214 offenen Ausgängen vollständig aus und wäre es nicht — genau die Fehlerklasse, gegen die
 * dieser Loop gebaut wird („ein Vorgang scheitert und sieht aus wie erledigt", CLAUDE.md).
 * Darum: die Gesamtzahl steht im Kopf, und sobald sie über der Zahl der Zeilen liegt, sagt die
 * Seite das ausdrücklich.
 *
 * ## Ältestes zuerst — und die Seite sortiert nicht selbst
 *
 * Die Reihenfolge entscheidet die Route (`order by created_at asc`, F1.4): eine
 * Zahlungserinnerung, die seit vier Tagen wartet, ist dringender als eine von heute Morgen. Die
 * Seite reicht diese Reihenfolge durch. Eine zweite Sortierung hier wäre eine zweite Wahrheit
 * über dieselbe Frage, und beim nächsten Deckel-Fall die falsche: sie sortierte nur die 200
 * geladenen Zeilen um und schöbe die ältesten dabei aus dem Blick.
 */
import { BewertungsbitteSchalter } from "../ui/BewertungsbitteSchalter";
import { useRef, useState, type RefObject } from "react";
import { Link } from "react-router-dom";
import { vorgangPfad } from "../lib/pfade";
import { apiMutate, useApi } from "../hooks/useApi";
import type {
  AusgangsEntscheidung,
  AusgangsListe,
  AusgangsSammelErgebnis,
  AusgangZeile,
} from "../ui/types";
import { PILL_CLASSES, formatDateTime, timeAgo } from "../ui/format";
import { mahnstufeText } from "../lib/mahnstufe-anzeige";
import { Button, Card, EmptyState, ErrorNote, Icon, LoadingRows, Modal, PageHeader, Zeichenzaehler, Checkbox, Textarea } from "../ui/components";
import { anlassName, type SaisonKampagne } from "../ui/SaisonKampagneDrawer";
import { TextbausteinPicker, insertAtCursor } from "../ui/TextbausteinPicker";
import { OFFENE_RECHNUNGSMAILS_URL, OffeneRechnungsmails, type OffeneRechnungsmail } from "../ui/abrechnung/OffeneRechnungsmails";

/**
 * Die sieben Klasse-A-Stellen aus sql/266 in der Sprache des Betreibers.
 *
 * Der Ton ist keine Dekoration: eine Mahnung ist eine andere Entscheidung als eine
 * Menü-Erinnerung, und der Betreiber soll das sehen, bevor er liest.
 */
const HERKUNFT: Record<string, { label: string; tone: keyof typeof PILL_CLASSES }> = {
  nachfassen: { label: "Nachfassen zum Angebot", tone: "info" },
  event_erinnerung: { label: "Erinnerung an den Termin", tone: "info" },
  restzahlung_erinnerung: { label: "Erinnerung an die Restzahlung", tone: "warn" },
  ueberfaellig_mahnung: { label: "Mahnung wegen Überfälligkeit", tone: "neutral" },
  menue_erinnerung: { label: "Erinnerung an die Menüwahl", tone: "info" },
  rechnung: { label: "Rechnung", tone: "primary" },
  gutschrift: { label: "Gutschrift", tone: "primary" },
  rechnungsadresse_anfordern: { label: "Rechnungsadresse anfordern", tone: "info" },
  // Punkt 101 Stufe 2 (sql/524): Menüvorschlag an eine Saison-Vormerkung.
  saison_vorschlag: { label: "Saison-Vorschlag", tone: "primary" },
  // Google-Bewertungsbitte (sql/542): drei Werktage nach der Veranstaltung, nur mit Einwilligung.
  bewertungsbitte: { label: "Bewertungsbitte", tone: "info" },
  // Buchungsstrecke C3 (sql/597): „Ihr Fest rückt näher“, zwei Wochen vor der Veranstaltung.
  vorfreude: { label: "Vorfreude-Mail (zwei Wochen vorher)", tone: "info" },
};

/** Punkt 101 Stufe 2: die Kampagne steckt im Schlüssel `saison:<kampagne>:<vormerkung>`. */
const kampagneAus = (schluessel: string) => (schluessel.startsWith("saison:") ? (schluessel.split(":")[1] ?? "") : "");

/**
 * Eine unbekannte Herkunft wird ROH angezeigt, nicht verschwiegen.
 *
 * Käme eines Tages eine achte Stelle dazu und fehlte hier, dann stünde ihre Zeile sonst ohne
 * jede Kennzeichnung da — der Betreiber entschiede über eine Mail, deren Anlass die Oberfläche
 * ihm verschwiegen hat. Der rohe Wert ist hässlich und ehrlich; das ist die richtige Reihenfolge.
 */
function herkunftMeta(herkunft: string): { label: string; tone: keyof typeof PILL_CLASSES } {
  return HERKUNFT[herkunft] ?? { label: herkunft, tone: "neutral" };
}

/**
 * Schritt 7 (KONZEPT-VERSANDMODUS-UND-TEXTBAUSTEINE.md § 4.5): die vier Erinnerungs-Kategorien,
 * deren Wortlaut sich vor der Freigabe bearbeiten lässt. Rechnung/Gutschrift bleiben reine Belege
 * ohne Editiertext; Menü-Erinnerung steht nicht in der Vier-Kategorien-Liste des Konzepts und
 * bleibt deshalb ebenfalls read-only. Dieselbe Liste steht serverseitig in sql/291 — dort ist sie
 * die eigentliche Grenze, hier entscheidet sie nur, was die Oberfläche anbietet.
 */
// sql/664 (Antoine, 04.10.2026, „Alle Rechnungsmails bearbeitbar“): dazu der Begleittext der
// Rechnungs- und Gutschrift-Mails. Bearbeitet wird nur der Mailtext, nie der Beleg selbst.
const EDITIERBARE_HERKUNFT = new Set(["nachfassen", "event_erinnerung", "restzahlung_erinnerung", "ueberfaellig_mahnung", "rechnung", "gutschrift"]);

/* ═══════════════════════════ F2.3 — die beiden Entscheidungen ═══════════════════════════════ */

/**
 * Die benannten Nein-Antworten aus sql/267 in der Sprache des Betreibers.
 *
 * WARUM ÜBERSETZT UND NICHT ROH: `zustand_versendet` ist für den Betreiber keine Auskunft. Was er
 * wissen muss, ist die Folge — „die ist schon draußen, Ihr Klick hat nichts mehr bewirkt".
 *
 * WARUM MIT ROHEM RÜCKFALL: käme eines Tages ein Grund dazu, der hier fehlt, dann stünde sonst
 * gar nichts da, und ein misslungener Klick sähe aus wie ein gelungener — genau die Fehlerklasse
 * dieses Repos („ein Vorgang scheitert und sieht aus wie erledigt", CLAUDE.md). Der rohe Code ist
 * hässlich und ehrlich.
 */
const GRUND_TEXT: Record<string, string> = {
  nicht_gefunden: "Nicht gefunden — der Ausgang gehört nicht zu diesem Betrieb oder wurde entfernt.",
  keine_berechtigung: "Keine Berechtigung — nur Inhaber dürfen entscheiden.",
  nicht_angemeldet: "Nicht angemeldet — bitte melden Sie sich neu an.",
  grund_fehlt: "Ohne Grund wird nichts verworfen.",
  zustand_freigegeben: "Ist bereits freigegeben und geht mit dem nächsten Versandlauf hinaus.",
  zustand_verworfen: "Wurde inzwischen verworfen — eine verworfene Nachricht geht nicht mehr hinaus.",
  zustand_versendet: "Ist bereits hinausgegangen.",
  zustand_fehlgeschlagen: "Der Versand ist fehlgeschlagen; diese Zeile braucht eine eigene Klärung.",
  ohne_antwort: "Die Datenbank hat kein verwertbares Ergebnis geliefert. Bitte erneut versuchen.",
  // Schritt 7 (§ 4.5): Antworten von `bearbeite_ausgang_text` (sql/291), sofern sie doch bis zur
  // Oberfläche durchdringen — die Editierbox bietet sich selbst nur an, wo sie erlaubt ist, aber
  // ein zweiter Tab oder ein veralteter Zustand kann diese Meldungen trotzdem auslösen.
  herkunft_nicht_editierbar: "Dieser Ausgang gehört zu keiner der vier bearbeitbaren Kategorien.",
  text_fehlt: "Ohne Text lässt sich nichts speichern.",
  // Saison-Mails (Antoine 26.09.2026): erste Kampagne einzeln, danach Stichprobe + Sammelfreigabe.
  saison_erste_kampagne_einzeln: "Erste Saison-Kampagne — bitte jede Mail einzeln lesen und freigeben.",
  saison_stichprobe_fehlt: "Erst eine Stichprobe einzeln freigeben, dann geht die Sammelfreigabe.",
  // Buchungsstrecke A1: überholte Nachfass-Vorschläge werden beim Freigeben verworfen, nicht gesendet.
  nachfassen_gast_hat_geantwortet: "Nicht gesendet und verworfen: Der Gast hat seit dem Angebotsversand geantwortet.",
  nachfassen_status_geaendert: "Nicht gesendet und verworfen: Der Vorgang steht nicht mehr auf „Angebot verschickt“.",
  // Buchungsstrecke A2: der Gast hat den Abmeldelink einer Nachfass-Mail geklickt.
  nachfassen_abgemeldet: "Nicht gesendet und verworfen: Der Gast hat sich von Erinnerungen zu diesem Angebot abgemeldet.",
  // Buchungsstrecke C2: die Erinnerung nennt eine Terminreservierung, die nicht mehr läuft.
  nachfassen_reservierung_abgelaufen: "Nicht gesendet und verworfen: Die Erinnerung nennt eine Terminreservierung, die inzwischen abgelaufen, ausgeschaltet oder geändert ist.",
  nachfassen_neu_versendet: "Nicht gesendet und verworfen: Das Angebot wurde nach dieser Erinnerung neu versendet.",
};

function grundText(grund: string): string {
  return GRUND_TEXT[grund] ?? grund;
}

/**
 * Ein Fehlschlag aus `apiMutate` wird lesbar gemacht, ohne je verschluckt zu werden.
 *
 * `toApiError` hängt den benannten Code der Route an (`code`) und nimmt als `message` das, was
 * der Server im Klartext geschrieben hat. Ein Code, den wir kennen, wird übersetzt; alles andere
 * geht wörtlich durch — auch ein „Failed to fetch". Ein `catch`, das hier still bliebe, wäre der
 * teuerste Fehler dieses Repos in seiner Frontend-Fassung.
 */
function fehlerText(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  if (typeof code === "string" && code in GRUND_TEXT) return GRUND_TEXT[code]!;
  return err instanceof Error ? err.message : String(err);
}

/**
 * Was aus der letzten Entscheidung wurde — je Kennung eine Zeile.
 *
 * WARUM DAS NICHT IN DER ZEILE STEHT: eine entschiedene Zeile verschwindet beim nächsten Laden
 * aus der Liste (sie ist nicht mehr `offen`). Stünde die Rückmeldung dort, wäre die einzige
 * Auskunft über den Ausgang, dass er weg ist — und das sieht bei „freigegeben" genauso aus wie
 * bei „ein anderer hat sie vor zwei Sekunden verworfen". Der Bericht steht deshalb über der
 * Liste und überlebt das Nachladen.
 */
interface Bericht {
  ton: "gut" | "warn" | "fehler";
  kopf: string;
  /** Leer bei einer Einzelentscheidung — dort sagt schon `kopf` alles. */
  zeilen: { betreff: string; ergebnis: string; ok: boolean }[];
}

const BERICHT_TON: Record<Bericht["ton"], { rahmen: string; icon: string }> = {
  gut: { rahmen: "border-secondary/40 bg-secondary/[0.07]", icon: "check_circle" },
  warn: { rahmen: "border-secondary/40 bg-secondary/[0.07]", icon: "warning" },
  fehler: { rahmen: "border-error/40 bg-error/[0.07]", icon: "error" },
};

function BerichtKarte({ bericht, onClose }: { bericht: Bericht; onClose: () => void }) {
  const ton = BERICHT_TON[bericht.ton];
  return (
    <div
      role="status"
      data-testid="freigabe-bericht"
      className={`mb-4 flex gap-3 rounded-xl border px-4 py-3 ${ton.rahmen}`}
    >
      <Icon name={ton.icon} className="mt-0.5 shrink-0 text-[18px]" />
      <div className="min-w-0 flex-1">
        <p className="font-body-md text-body-md text-on-background dark:text-inverse-on-surface">{bericht.kopf}</p>
        {bericht.zeilen.length > 0 && (
          // Je Kennung eine Zeile — auch die gelungenen. „Zwei von drei" ohne die Namen zwänge den
          // Betreiber, die Liste mit dem Gedächtnis abzugleichen.
          <ul className="mt-2 flex flex-col gap-1">
            {bericht.zeilen.map((z, i) => (
              <li
                key={`${z.betreff}-${i}`}
                className="flex gap-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim"
              >
                <Icon name={z.ok ? "check" : "close"} className="mt-0.5 shrink-0 text-[15px]" />
                <span className="min-w-0">
                  <span className="font-medium text-on-background dark:text-inverse-on-surface">{z.betreff}</span>
                  {" — "}
                  {z.ergebnis}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button
        type="button"
        aria-label="Meldung schließen"
        className="shrink-0 self-start text-secondary hover:text-on-background dark:text-secondary-fixed-dim"
        onClick={onClose}
      >
        <Icon name="close" className="text-[18px]" />
      </button>
    </div>
  );
}

/**
 * F2.2 — der Wortlaut, ungekürzt.
 *
 * WARUM UNGEKÜRZT: der Betreiber gibt frei, was ein Kunde lesen wird. Entschiede er über einen
 * Auszug, wäre die Freigabe wertlos — genau der Satz, an dem eine Mahnung zu scharf klingt oder
 * ein falscher Betrag steht, fiele aus der Vorschau heraus. Deshalb steht hier weder eine
 * Kürzung (`line-clamp`, „…") noch eine Zusammenfassung. `GET /api/ausgaenge` liefert `text` und
 * `html` bereits vollständig mit (F1.4), es ist also nichts nachzuladen.
 *
 * ## Warum der HTML-Teil in einem abgeschotteten Rahmen steht — und NICHT per innerHTML
 *
 * Der HTML-Teil ist das, was der Kunde tatsächlich sieht; ihn als Quelltext zu zeigen hieße, dem
 * Betreiber `<p>Guten Tag …</p>` vorzulegen und ihn über etwas entscheiden zu lassen, das er so
 * nie zu Gesicht bekommt. Er wird also gerendert.
 *
 * Gerendert wird er aber NICHT mit `dangerouslySetInnerHTML`: der Text entsteht zwar in MAESTRO,
 * trägt aber durchgehend Fremdinhalt — Kundenname, Firmenname, Veranstaltungstitel, Notizen aus
 * dem Anfrageformular. Ein `<img onerror=…>` in einem Firmennamen liefe damit im Dokument des
 * Betreibers, mit seinem angemeldeten Zustand. Das ganze Repo hält diese Regel schon
 * (`pages/inquiries.tsx`: Gasttext wird als React-Kind gerendert, nie als HTML).
 *
 * Der Ausweg ist ein `iframe` mit `srcDoc` und **leerem `sandbox`** — die schärfste Stufe: keine
 * Skripte (`allow-scripts` fehlt), kein Zugriff auf Dokument/Cookies der Anwendung
 * (`allow-same-origin` fehlt), keine Formulare, keine Navigation des Hauptfensters. Der Inhalt
 * kann angezeigt werden und sonst nichts. `referrerPolicy="no-referrer"` kommt dazu, damit ein
 * Zählpixel im Mailtext nicht die Adresse der Freigabeseite an einen Dritten meldet.
 *
 * **Preis dieser Entscheidung, bewusst bezahlt:** ohne `allow-same-origin` kann die Seite die
 * Höhe des Inhalts nicht messen, der Rahmen bekommt also eine feste Höhe und scrollt innen. Das
 * ist keine Kürzung — der vollständige Wortlaut steht im `srcdoc` und ist erreichbar —, aber es
 * ist der Grund, warum hier keine automatische Höhe steht. Sicherheit vor Bequemlichkeit.
 *
 * ## Schritt 7 — Editieren für die vier Erinnerungs-Kategorien
 *
 * Für `EDITIERBARE_HERKUNFT` lässt sich die Textfassung über einen „Bearbeiten"-Knopf in ein
 * `<textarea>` mit `TextbausteinPicker` darüber verwandeln, vorbefüllt mit dem sweep-generierten
 * Text — „Freigeben sendet dann den ggf. bearbeiteten Text statt zwingend des Originals" (Konzept
 * § 4.5, wörtlich). **Bewusst NICHT sofort ein Textarea** (Konzept § 4.5 letzter Absatz lässt die
 * Aufklapp-vs-immer-offen-Frage ausdrücklich offen): der Wortlaut ist zunächst wie an jeder
 * anderen Stelle ein reiner `<pre>`-Lesetext (F2.2-Vertrag: Zeichen für Zeichen identisch mit der
 * Warteschlange), „Bearbeiten" ist ein bewusster Zusatzschritt, kein Default.
 *
 * **Bewusste Vereinfachung:** die HTML-Vorschau darunter bleibt in jedem Fall read-only und zeigt
 * weiterhin den ursprünglichen, sweep-generierten Wortlaut — sie aktualisiert sich NICHT live beim
 * Tippen. Der Server leitet `html_inhalt` beim Freigeben serverseitig aus dem bearbeiteten Text ab
 * (`routes/ausgaenge.ts#textZuHtml`), sodass das, was beim Kunden ankommt, den bearbeiteten Text
 * trägt — nur die Live-Vorschau hier hinkt hinterher. Ein Hinweistext sagt das ausdrücklich.
 */
function Wortlaut({
  ausgang,
  editierbar,
  bearbeitenModus,
  onBearbeitenModusChange,
  text,
  onTextChange,
  textareaRef,
}: {
  ausgang: AusgangZeile;
  editierbar: boolean;
  bearbeitenModus: boolean;
  onBearbeitenModusChange: (v: boolean) => void;
  text: string;
  onTextChange: (v: string) => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
}) {
  const anhaenge = ausgang.anhang ?? [];
  return (
    <div className="mt-3 flex flex-col gap-4 rounded-xl border border-[#F0E7D6] bg-[#FDFBF7] px-4 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
      {/* Zustellangaben zuerst: sie gehören zum Ausgang genauso wie der Text. Wer eine Rechnung
          freigibt, ohne zu sehen, dass ein PDF daranhängt, entscheidet nur über die halbe Mail. */}
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        <dt className="font-medium">An</dt>
        <dd className="min-w-0 break-words">{ausgang.empfaenger}</dd>
        {ausgang.cc && (
          <>
            <dt className="font-medium">Kopie</dt>
            <dd className="min-w-0 break-words">{ausgang.cc}</dd>
          </>
        )}
        {ausgang.antwortAn && (
          <>
            <dt className="font-medium">Antwort an</dt>
            <dd className="min-w-0 break-words">{ausgang.antwortAn}</dd>
          </>
        )}
        {anhaenge.length > 0 && (
          <>
            <dt className="font-medium">Anhang</dt>
            <dd className="min-w-0 break-words">{anhaenge.map((a) => a.dateiname).join(", ")}</dd>
          </>
        )}
      </dl>

      <section className="flex min-w-0 flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-label-md text-[11px] uppercase tracking-wide text-secondary dark:text-secondary-fixed-dim">
            Textfassung
          </h4>
          <div className="flex items-center gap-2">
            {editierbar && bearbeitenModus && (
              <TextbausteinPicker onInsert={(content) => insertAtCursor(textareaRef.current, text, onTextChange, content)} />
            )}
            {editierbar && !bearbeitenModus && (
              <button
                type="button"
                className="font-body-sm text-body-sm text-primary underline-offset-2 hover:underline"
                onClick={() => onBearbeitenModusChange(true)}
              >
                Bearbeiten
              </button>
            )}
          </div>
        </div>
        {ausgang.herkunft === "saison_vorschlag" && (
          <p className="font-body-sm text-[11.5px] italic text-secondary dark:text-secondary-fixed-dim">
            Preis und Menü kommen aus dem Paket und sind hier nicht änderbar. Den Text ändern Sie in der Kampagne unter Kunden → Vormerkungen.
          </p>
        )}
        {editierbar && bearbeitenModus ? (
          <>
            <Textarea
              ref={textareaRef}
              className="min-h-[160px] whitespace-pre-wrap font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface"
              value={text}
              onChange={(e) => onTextChange(e.target.value)}
            />
            <p className="font-body-sm text-[11.5px] italic text-secondary dark:text-secondary-fixed-dim">
              Freigeben sendet diesen Text. Die Vorschau unten zeigt weiterhin den ursprünglichen Wortlaut.
            </p>
          </>
        ) : text ? (
          // `whitespace-pre-wrap`: Absätze und Zeilenumbrüche einer Mail tragen Bedeutung. Kein
          // `max-h` — die Seite scrollt, der Text nicht in einem Kästchen.
          <pre className="whitespace-pre-wrap break-words font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
            {text}
          </pre>
        ) : (
          // Eine leere Fläche sähe aus wie „nichts drin"; hier steht, dass es diesen Teil nicht gibt.
          <p className="font-body-sm text-body-sm italic text-secondary dark:text-secondary-fixed-dim">
            Dieser Ausgang hat keine Textfassung.
          </p>
        )}
      </section>

      <section className="flex min-w-0 flex-col gap-1.5">
        <h4 className="font-label-md text-[11px] uppercase tracking-wide text-secondary dark:text-secondary-fixed-dim">
          So sieht es der Empfänger
        </h4>
        {ausgang.html ? (
          <iframe
            title={`Wortlaut: ${ausgang.betreff}`}
            srcDoc={ausgang.html}
            sandbox=""
            referrerPolicy="no-referrer"
            className="h-[420px] w-full rounded-lg border border-[#F0E7D6] bg-white dark:border-white/[0.06]"
          />
        ) : (
          <p className="font-body-sm text-body-sm italic text-secondary dark:text-secondary-fixed-dim">
            Dieser Ausgang hat keine HTML-Fassung.
          </p>
        )}
      </section>
    </div>
  );
}

function Zeile({
  ausgang,
  gewaehlt,
  onWahl,
  onFreigeben,
  onVerwerfen,
  beschaeftigt,
  saison,
}: {
  ausgang: AusgangZeile;
  /** Saison-Vorschlag: Sprache der Mail und ob es ein Nachzügler ist (aus GET /api/saison-kampagnen). */
  saison?: { sprache: "de" | "en"; nachzuegler: boolean };
  gewaehlt: boolean;
  onWahl: (an: boolean) => void;
  /** Schritt 7: `bearbeiteterText` ist `undefined`, solange der Betreiber die Textfassung nicht
   *  angefasst hat (kein Body → identisches Verhalten wie vor Schritt 7, Regressionsschutz). */
  onFreigeben: (bearbeiteterText?: string) => void;
  onVerwerfen: () => void;
  /** Solange eine Entscheidung läuft, sind alle Knöpfe gesperrt — ein zweiter Klick auf
   *  „Freigeben" wäre harmlos (der Übergang ist bedingt), ein zweiter auf „Verwerfen" öffnete
   *  aber einen zweiten Dialog über derselben Zeile. */
  beschaeftigt: boolean;
}) {
  const meta = herkunftMeta(ausgang.herkunft);
  /**
   * Z3.3 — die Mahnstufe, sofern die Zeile eine trägt.
   *
   * Sie steht neben der Herkunft und nicht in ihr: die Herkunft sagt, WORUM es geht, die Stufe
   * WIE WEIT es ist. Seit Z3.2 liegen erste und zweite Mahnung als getrennte Vorschläge in dieser
   * Liste — ohne diesen Zusatz sind sie nicht zu unterscheiden, und der Betreiber gibt die letzte
   * Mahnung frei im Glauben, es sei die erste.
   */
  const stufe = mahnstufeText(ausgang.schluessel);
  // Zugeklappt ist die Vorgabe: bei 200 Zeilen wären 200 offene Mailtexte keine Liste mehr.
  const [offen, setOffen] = useState(false);

  /**
   * Schritt 7 (§ 4.5) — Editierzustand lebt auf Zeilenebene, nicht im Wortlaut-Aufklapper: der
   * Freigeben-Knopf steht IMMER da, unabhängig davon, ob „Wortlaut anzeigen" je angeklickt wurde,
   * und muss den zuletzt bearbeiteten Text auch dann kennen, wenn der Block gerade zugeklappt ist.
   */
  const editierbar = EDITIERBARE_HERKUNFT.has(ausgang.herkunft) && ausgang.text != null;
  const [text, setText] = useState(ausgang.text ?? "");
  const [bearbeitenModus, setBearbeitenModus] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const original = (ausgang.text ?? "").trim();
  const bearbeitet = editierbar && text.trim() !== original;

  return (
    <li className="flex flex-col gap-2 border-b border-[#F6F0E4] px-[18px] py-3.5 last:border-b-0 dark:border-white/[0.05]">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        {/* Die Auswahl gehört zur Zeile, nicht in eine zweite Liste daneben: was ausgewählt ist,
            muss neben dem stehen, was es auswählt. */}
        <Checkbox
          aria-label={`„${ausgang.betreff}“ auswählen`}
          checked={gewaehlt}
          disabled={beschaeftigt}
          onChange={(e) => onWahl(e.target.checked)}
          className="mt-1 shrink-0 self-start sm:mt-0 sm:self-auto"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-body-md text-body-md font-medium text-on-background dark:text-inverse-on-surface">
            {ausgang.betreff}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className={`rounded-full px-2 py-0.5 font-label-md text-[10.5px] ${PILL_CLASSES[meta.tone]}`}>
              {meta.label}
              {stufe ? ` · ${stufe}` : ""}
            </span>
            {saison?.sprache === "en" && (
              <span className={`rounded-full px-2 py-0.5 font-label-md text-[10.5px] ${PILL_CLASSES.info}`}>Englisch</span>
            )}
            {saison?.nachzuegler && (
              <span className={`rounded-full px-2 py-0.5 font-label-md text-[10.5px] ${PILL_CLASSES.neutral}`}>Nachzügler</span>
            )}
            <span className="inline-flex min-w-0 items-center gap-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              <Icon name="mail" className="text-[14px]" />
              <span className="truncate">{ausgang.empfaenger}</span>
            </span>
            {/* Punkt 115 (Paket 3): zu welchem Fall gehört die Erinnerung? Ein Klick führt hin. */}
            {ausgang.eventId && (
              <Link
                to={vorgangPfad(ausgang.eventId)}
                className="inline-flex items-center gap-1 rounded-md font-body-sm text-body-sm font-semibold text-primary hover:underline dark:text-primary-fixed"
              >
                <Icon name="open_in_new" className="text-[14px]" /> Zum Vorgang
              </Link>
            )}
          </div>
          {/* Google-Bewertungsbitte (sql/542): der neutrale Schalter steht an der Zeile, nicht erst im Wortlaut. */}
          {ausgang.herkunft === "bewertungsbitte" && ausgang.eventId && (
            <div className="mt-2 max-w-md">
              <BewertungsbitteSchalter eventId={ausgang.eventId} kompakt />
            </div>
          )}
        </div>
        {/* Das Alter steht relativ da, weil danach entschieden wird („wartet seit vier Tagen");
            der genaue Zeitpunkt hängt als `title` daran, damit die Angabe nachprüfbar bleibt. */}
        <span
          className="shrink-0 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim"
          title={formatDateTime(ausgang.angelegtAm)}
        >
          {timeAgo(ausgang.angelegtAm)}
        </span>
        {/* Der Weg zum Wortlaut. Er steht an JEDER Zeile, auch wenn Text und HTML fehlen sollten —
            dass ein Ausgang leer ist, gehört zu dem, was der Betreiber vor der Freigabe sehen muss. */}
        <button
          type="button"
          aria-expanded={offen}
          className="shrink-0 self-start font-body-sm text-body-sm text-primary underline-offset-2 hover:underline sm:self-auto"
          onClick={() => setOffen((v) => !v)}
        >
          {offen ? "Wortlaut ausblenden" : "Wortlaut anzeigen"}
        </button>
        {/* Die zwei Knöpfe (F2.3). Sie stehen an JEDER Zeile, unabhängig davon, ob der Wortlaut
            aufgeklappt ist — GATE-F3: „einzeln, mit Sammelfreigabe", einzeln ist der Normalfall.
            Sie werden auch nicht nach Rolle versteckt: über die Berechtigung entscheidet die
            Route (sql/267 über `POST /api/ausgaenge/:id/...`). Ein Browser, der die Rolle ein
            zweites Mal auslegt, hat in einem halben Jahr die veraltete Wahrheit — und ein `staff`
            kommt ohnehin nicht bis hierher, weil `GET /api/ausgaenge` ihm 403 antwortet. */}
        <div className="flex shrink-0 gap-2">
          {/* E9: je Zeile secondary — der orange Hauptknopf ist die Sammelaktion (sonst bei vielen
              Zeilen ein Meer aus Orange). */}
          <Button
            variant="secondary"
            className="!py-1.5"
            disabled={beschaeftigt}
            onClick={() => onFreigeben(bearbeitet ? text.trim() : undefined)}
          >
            <Icon name="send" className="text-[16px]" /> Freigeben
          </Button>
          <Button variant="secondary" className="!py-1.5" disabled={beschaeftigt} onClick={onVerwerfen}>
            <Icon name="block" className="text-[16px]" /> Verwerfen
          </Button>
        </div>
      </div>
      {offen && (
        <Wortlaut
          ausgang={ausgang}
          editierbar={editierbar}
          bearbeitenModus={bearbeitenModus}
          onBearbeitenModusChange={setBearbeitenModus}
          text={text}
          onTextChange={setText}
          textareaRef={textareaRef}
        />
      )}
    </li>
  );
}

/**
 * Der Grund beim Verwerfen — eine Rückfrage, kein `window.prompt`.
 *
 * WARUM ÜBERHAUPT EIN GRUND: `verwirf_ausgang` (sql/267) weigert sich ohne ihn, und das ist die
 * richtige Reihenfolge. Verwerfen heißt, dass ein Kunde eine Mahnung, Rechnung oder Erinnerung
 * NIE bekommt. Ohne festgehaltenen Grund könnte in vier Wochen niemand mehr sagen, warum — und
 * die Frage kommt, sobald jemand nachhakt, wo seine Rechnung bleibt.
 *
 * Der Knopf bleibt gesperrt, solange das Feld leer ist. Ein Klick, der an der Datenbank in
 * `grund_fehlt` läuft, wäre die schlechtere Fassung derselben Regel: dieselbe Antwort, eine
 * Rundreise später.
 */
function VerwerfenDialog({
  ausgang,
  laeuft,
  onAbbrechen,
  onVerwerfen,
}: {
  ausgang: AusgangZeile;
  laeuft: boolean;
  onAbbrechen: () => void;
  onVerwerfen: (grund: string) => void;
}) {
  const [grund, setGrund] = useState("");
  const leer = grund.trim().length === 0;
  return (
    <Modal
      open
      onClose={() => !laeuft && onAbbrechen()}
      width="520px"
      testId="verwerfen-dialog"
      title={<>Verwerfen: „{ausgang.betreff}“</>}
      footer={
        <>
          <Button variant="secondary" disabled={laeuft} onClick={onAbbrechen}>
            Abbrechen
          </Button>
          <Button variant="danger" disabled={laeuft || leer} onClick={() => onVerwerfen(grund.trim())}>
            <Icon name="block" className="text-[16px]" /> {laeuft ? "Verwirft …" : "Verwerfen"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          Diese Nachricht geht nie an {ausgang.empfaenger}. Der Grund bleibt am Ausgang stehen, damit später
          nachvollziehbar ist, warum nichts hinausging.
        </p>
        <label className="block">
          <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
            Grund *
          </span>
          <Textarea
            className="min-h-[96px]"
            maxLength={1000}
            autoFocus
            placeholder="z. B. Kundin hat gestern telefonisch bezahlt"
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
          />
          <Zeichenzaehler current={grund.length} max={1000} />
        </label>
      </div>
    </Modal>
  );
}

export default function FreigabenPage() {
  const { data, loading, error, reload } = useApi<AusgangsListe>("/api/ausgaenge");
  const rechnungsmailsQ = useApi<OffeneRechnungsmail[]>(OFFENE_RECHNUNGSMAILS_URL);
  const ausgaenge = data?.ausgaenge ?? [];
  // `gesamt` kommt vom Server; ohne Antwort wird nichts geschätzt (eine erfundene 0 wäre eine
  // Aussage über etwas, das die Seite nicht weiß).
  const gesamt = data?.gesamt ?? null;
  const zurueckgehalten = gesamt === null ? 0 : gesamt - ausgaenge.length;

  const [auswahl, setAuswahl] = useState<string[]>([]);
  const [bericht, setBericht] = useState<Bericht | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [verwerfenId, setVerwerfenId] = useState<string | null>(null);
  // Punkt 101 Stufe 2: Saison-Vorschläge als eigene Gruppe je Kampagne, mit „Alle N freigeben“.
  const kampagnenQ = useApi<SaisonKampagne[]>("/api/saison-kampagnen");
  const saisonGruppen = [...new Set(ausgaenge.filter((a) => a.herkunft === "saison_vorschlag").map((a) => kampagneAus(a.schluessel)))].map((kid) => ({
    kid,
    kampagne: (kampagnenQ.data ?? []).find((k) => k.id === kid) ?? null,
    zeilen: ausgaenge.filter((a) => a.herkunft === "saison_vorschlag" && kampagneAus(a.schluessel) === kid),
  }));
  const weitere = ausgaenge.filter((a) => a.herkunft !== "saison_vorschlag");
  const [alleBestaetigen, setAlleBestaetigen] = useState<{ titel: string; ids: string[] } | null>(null);
  const zuVerwerfen = ausgaenge.find((a) => a.id === verwerfenId) ?? null;

  // Nur Zeilen zählen, die es noch gibt: nach einem Nachladen kann eine ausgewählte Zeile
  // entschieden und damit aus der `offen`-Liste verschwunden sein. Eine Auswahl über eine Zeile,
  // die niemand mehr sieht, wäre eine unsichtbare Entscheidung.
  const gewaehlt = auswahl.filter((id) => ausgaenge.some((a) => a.id === id));
  const alleGewaehlt = weitere.length > 0 && weitere.every((a) => gewaehlt.includes(a.id));

  const weitereGewaehlt = gewaehlt.filter((id) => weitere.some((a) => a.id === id));

  function betreffVon(id: string): string {
    // Rückfall auf die rohe Kennung: lieber eine UUID als eine verschwiegene Zeile.
    return ausgaenge.find((a) => a.id === id)?.betreff ?? id;
  }

  /**
   * Eine einzelne Entscheidung: freigeben oder verwerfen.
   *
   * Schritt 7 (§ 4.5): `bearbeiteterText` ist nur beim Freigeben gesetzt, und nur, wenn die Zeile
   * ihn tatsächlich verändert hat (`Zeile` schickt sonst `undefined`) — kein Body bleibt weiterhin
   * das Verhalten von vor Schritt 7 (Regressionsschutz wie bei Schritt 5/6).
   */
  async function entscheide(
    ausgang: AusgangZeile,
    art: "freigeben" | "verwerfen",
    grund?: string,
    bearbeiteterText?: string,
  ): Promise<void> {
    setLaeuft(true);
    try {
      const e = await apiMutate<AusgangsEntscheidung>(
        `/api/ausgaenge/${ausgang.id}/${art}`,
        "POST",
        art === "verwerfen" ? { grund } : bearbeiteterText !== undefined ? { text: bearbeiteterText } : undefined,
      );
      // `geaendert: false` ist KEIN Erfolg dieses Klicks — jemand anderes (oder derselbe Klick
      // zweimal) war schneller. Das steht so da, statt eine fremde Entscheidung als die eigene
      // auszugeben.
      setBericht(
        e.geaendert
          ? {
              ton: "gut",
              kopf:
                art === "freigeben"
                  ? `„${ausgang.betreff}“ ist freigegeben und geht mit dem nächsten Versandlauf hinaus.`
                  : `„${ausgang.betreff}“ ist verworfen und geht nicht hinaus.`,
              zeilen: [],
            }
          : {
              ton: "warn",
              kopf: `„${ausgang.betreff}“ war bereits ${e.zustand} — Ihr Klick hat daran nichts geändert.`,
              zeilen: [],
            },
      );
      setVerwerfenId(null);
      setAuswahl((a) => a.filter((id) => id !== ausgang.id));
      reload();
    } catch (err) {
      setBericht({ ton: "fehler", kopf: `„${ausgang.betreff}“ — ${fehlerText(err)}`, zeilen: [] });
      // A1: ein überholter Nachfass-Vorschlag wurde serverseitig verworfen — die Liste neu laden,
      // sonst stünde er weiter als „offen“ da.
      const code = (err as { code?: unknown } | null)?.code;
      if (typeof code === "string" && code.startsWith("nachfassen_")) reload();
    } finally {
      setLaeuft(false);
    }
  }

  /**
   * Die Sammelfreigabe (GATE-F3) — und ihr Bericht je Kennung.
   *
   * Die Route antwortet mit 200, auch wenn nur ein Teil durchging. Ein „3 freigegeben" ohne die
   * Aufschlüsselung wäre hier die gefährlichste Anzeige überhaupt: der Betreiber ginge davon aus,
   * dass die Mahnung dabei war, die in Wahrheit an `zustand_verworfen` gescheitert ist.
   */
  async function sammelFreigeben(auswahlIds: string[] = gewaehlt): Promise<void> {
    const ids = auswahlIds;
    if (ids.length === 0) return;
    setLaeuft(true);
    try {
      const e = await apiMutate<AusgangsSammelErgebnis>("/api/ausgaenge/freigeben", "POST", { ids });
      const zeilen = e.ergebnisse.map((r) => ({
        betreff: betreffVon(r.id),
        ok: r.ok && r.geaendert,
        ergebnis: r.ok
          ? r.geaendert
            ? "freigegeben"
            : `war bereits ${r.zustand}`
          : grundText(r.grund),
      }));
      const misslungen = zeilen.filter((z) => !z.ok).length;
      setBericht({
        ton: misslungen === 0 ? "gut" : e.freigegeben === 0 ? "fehler" : "warn",
        kopf: `${e.freigegeben} von ${ids.length} freigegeben${misslungen > 0 ? `, ${misslungen} nicht:` : "."}`,
        zeilen,
      });
      setAuswahl((alt) => alt.filter((id) => !ids.includes(id)));
      setAlleBestaetigen(null);
      reload();
      kampagnenQ.reload();
    } catch (err) {
      setBericht({ ton: "fehler", kopf: `Sammelfreigabe fehlgeschlagen: ${fehlerText(err)}`, zeilen: [] });
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Freigaben"
        subtitle={
          gesamt === null
            ? "Was MAESTRO verschicken würde — erst nach Ihrer Freigabe."
            : gesamt === 0
              ? "Nichts wartet auf Freigabe."
              : `${gesamt === 1 ? "1 Ausgang wartet" : `${gesamt} Ausgänge warten`} auf Freigabe. Ältestes zuerst.`
        }
      />

      {error && <ErrorNote message={error} onRetry={reload} />}

      {bericht && <BerichtKarte bericht={bericht} onClose={() => setBericht(null)} />}

      {/* Abrechnung Stufe 2: ungesendete Rechnungsmails aus der Abrechnung, derselbe Tor-2-Weg. */}
      <OffeneRechnungsmails q={rechnungsmailsQ} />

      {loading ? (
        <Card>
          <LoadingRows rows={5} />
        </Card>
      ) : !error && ausgaenge.length === 0 && (rechnungsmailsQ.data ?? []).length > 0 ? null : !error && ausgaenge.length === 0 ? (
        <Card>
          <EmptyState
            icon="mark_email_read"
            title="Nichts wartet auf Freigabe"
            hint="Sobald MAESTRO eine Erinnerung, Mahnung, Rechnung oder Gutschrift vorbereitet, steht sie hier — und geht erst hinaus, wenn Sie sie freigeben."
          />
        </Card>
      ) : ausgaenge.length > 0 ? (
        <>
        {saisonGruppen.map((g) => {
          const titel = g.kampagne ? `${anlassName(g.kampagne.anlass)} ${g.kampagne.jahr}` : "Saison";
          const gIds = g.zeilen.map((a) => a.id);
          const gGewaehlt = gewaehlt.filter((id) => gIds.includes(id));
          // Antoine 26.09.2026: erste Kampagne einzeln, danach Stichprobe + Sammelfreigabe (der Server sperrt ebenso).
          const fg = g.kampagne?.freigabe;
          const sammelErlaubt = fg?.sammelErlaubt ?? false;
          const zusatz = new Map((g.kampagne?.vorschlaege ?? []).map((v) => [v.id, v]));
          const anzahlEn = g.zeilen.filter((a) => zusatz.get(a.id)?.sprache === "en").length;
          return (
            <Card key={g.kid} className="mb-4 flex flex-col overflow-hidden" data-testid="saison-gruppe">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[#F2ECDF] px-[18px] py-3.5 dark:border-white/[0.06]">
                <h3 className="flex items-center gap-2 font-headline-sm text-[15px] font-bold text-on-background dark:text-inverse-on-surface">
                  <Icon name="celebration" className="text-[18px] text-primary" /> Saison-Vorschläge · {titel}
                </h3>
                <span className={`rounded-full px-2 py-0.5 font-label-md text-[10.5px] ${PILL_CLASSES.warn}`}>{g.zeilen.length} offen</span>
                <p className="w-full font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  {`Menüvorschlag${g.kampagne?.paketName ? ` „${g.kampagne.paketName}“` : ""} an alle, die sich für ${titel.replace(/ \d+$/, "")} vorgemerkt haben. Direkt vor dem Versand prüft MAESTRO noch einmal, ob sich jemand abgemeldet hat.`}
                  {anzahlEn > 0 && ` ${anzahlEn === 1 ? "Eine Mail geht" : `${anzahlEn} Mails gehen`} auf Englisch hinaus — Wortlaut in der Zeile, Preise und Menü wie im Deutschen.`}
                </p>
              </div>
              {fg && !sammelErlaubt && (
                <div data-testid="saison-freigabe-stufe" className="flex gap-2.5 border-b border-[#F2ECDF] bg-secondary/[0.07] px-[18px] py-3 dark:border-white/[0.06]">
                  <Icon name="rule" className="mt-0.5 shrink-0 text-[18px] text-secondary dark:text-[#b8ae9e]" />
                  <p className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                    {fg.ersteKampagne ? (
                      <>
                        <b>Erste Saison-Kampagne:</b> bitte jede Mail einzeln lesen und freigeben. Ab der nächsten Kampagne genügt eine Stichprobe, danach geht die
                        Sammelfreigabe.
                      </>
                    ) : (
                      <>
                        <b>Stichprobe:</b> {`${Math.min(fg.geprueft, fg.stichprobe)} von ${fg.stichprobe} Mails einzeln gelesen und freigegeben.`} Danach lassen sich die
                        übrigen gesammelt freigeben.
                      </>
                    )}
                  </p>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 border-b border-[#F2ECDF] px-[18px] py-3 dark:border-white/[0.06]">
                <label className="flex items-center gap-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  <Checkbox
                    aria-label={`Alle Saison-Vorschläge ${titel} auswählen`}
                    checked={gGewaehlt.length === gIds.length}
                    disabled={laeuft}
                    onChange={(e) =>
                      setAuswahl((alt) => (e.target.checked ? [...new Set([...alt, ...gIds])] : alt.filter((id) => !gIds.includes(id))))
                    }
                  />
                  Alle auswählen
                </label>
                <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  {gGewaehlt.length === 0 ? "Nichts ausgewählt" : `${gGewaehlt.length} ausgewählt`}
                </span>
                <span className="ml-auto flex gap-2">
                  <Button variant="secondary" className="!py-1.5" disabled={laeuft || !sammelErlaubt || gGewaehlt.length === 0} onClick={() => void sammelFreigeben(gGewaehlt)}>
                    {gGewaehlt.length ? `${gGewaehlt.length} freigeben` : "Auswahl freigeben"}
                  </Button>
                  <Button variant="primary" className="!py-1.5" disabled={laeuft || !sammelErlaubt} onClick={() => setAlleBestaetigen({ titel, ids: gIds })}>
                    <Icon name="send" className="text-[16px]" /> {`Alle ${gIds.length} freigeben`}
                  </Button>
                </span>
              </div>
              <ul className="flex flex-col">
                {g.zeilen.map((a) => (
                  <Zeile
                    key={a.id}
                    ausgang={a}
                    saison={zusatz.get(a.id)}
                    gewaehlt={gewaehlt.includes(a.id)}
                    onWahl={(an) => setAuswahl((alt) => (an ? [...alt, a.id] : alt.filter((id) => id !== a.id)))}
                    onFreigeben={() => void entscheide(a, "freigeben")}
                    onVerwerfen={() => setVerwerfenId(a.id)}
                    beschaeftigt={laeuft}
                  />
                ))}
              </ul>
            </Card>
          );
        })}
        {weitere.length > 0 && (
        <Card className="flex flex-col overflow-hidden">
          {saisonGruppen.length > 0 && (
            <div className="flex items-center gap-3 border-b border-[#F2ECDF] px-[18px] py-3.5 dark:border-white/[0.06]">
              <h3 className="flex items-center gap-2 font-headline-sm text-[15px] font-bold text-on-background dark:text-inverse-on-surface">
                <Icon name="outgoing_mail" className="text-[18px]" /> Weitere Freigaben
              </h3>
              <span className={`rounded-full px-2 py-0.5 font-label-md text-[10.5px] ${PILL_CLASSES.neutral}`}>{weitere.length} offen</span>
            </div>
          )}
          {/* Die Sammelleiste (GATE-F3). Sie steht immer da, nicht erst nach der ersten Auswahl:
              sonst wäre der einzige Hinweis darauf, dass es eine Sammelfreigabe gibt, ein
              Kästchen, das man erst anklicken muss, um davon zu erfahren. */}
          <div className="flex flex-wrap items-center gap-3 border-b border-[#F2ECDF] px-[18px] py-3 dark:border-white/[0.06]">
            <label className="flex items-center gap-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              <Checkbox
                aria-label="Alle auswählen"
                checked={alleGewaehlt}
                disabled={laeuft}
                onChange={(e) =>
                  setAuswahl((alt) => {
                    const ids = weitere.map((a) => a.id);
                    return e.target.checked ? [...new Set([...alt, ...ids])] : alt.filter((id) => !ids.includes(id));
                  })
                }
              />
              Alle auswählen
            </label>
            <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              {weitereGewaehlt.length === 0 ? "Nichts ausgewählt" : `${weitereGewaehlt.length} ausgewählt`}
            </span>
            <Button
              variant="primary"
              className="ml-auto !py-1.5"
              disabled={laeuft || weitereGewaehlt.length === 0}
              onClick={() => void sammelFreigeben(weitereGewaehlt)}
            >
              <Icon name="send" className="text-[16px]" />
              {laeuft ? "Gibt frei …" : weitereGewaehlt.length === 0 ? "Auswahl freigeben" : `${weitereGewaehlt.length} freigeben`}
            </Button>
          </div>
          <ul className="flex flex-col">
            {weitere.map((a) => (
              <Zeile
                key={a.id}
                ausgang={a}
                gewaehlt={gewaehlt.includes(a.id)}
                onWahl={(an) => setAuswahl((alt) => (an ? [...alt, a.id] : alt.filter((id) => id !== a.id)))}
                onFreigeben={(bearbeiteterText) => void entscheide(a, "freigeben", undefined, bearbeiteterText)}
                onVerwerfen={() => setVerwerfenId(a.id)}
                beschaeftigt={laeuft}
              />
            ))}
          </ul>
          {/* Der Deckel der Route wird ausgesprochen, nicht verschwiegen. */}
          {zurueckgehalten > 0 && (
            <p className="flex items-center gap-1.5 border-t border-[#F2ECDF] px-[18px] py-3 font-body-sm text-body-sm text-secondary dark:border-white/[0.06] dark:text-secondary-fixed-dim">
              <Icon name="info" className="text-[15px]" />
              {`Angezeigt werden die ${ausgaenge.length} ältesten von ${gesamt}. Die übrigen ${zurueckgehalten} erscheinen, sobald diese entschieden sind.`}
            </p>
          )}
        </Card>
        )}
        </>
      ) : null}

      {alleBestaetigen && (
        <Modal
          open
          onClose={() => !laeuft && setAlleBestaetigen(null)}
          width="480px"
          testId="saison-alle-bestaetigen"
          title={`${alleBestaetigen.ids.length} Menüvorschläge freigeben?`}
          footer={
            <>
              <Button variant="secondary" disabled={laeuft} onClick={() => setAlleBestaetigen(null)}>
                Abbrechen
              </Button>
              <Button variant="primary" disabled={laeuft} onClick={() => void sammelFreigeben(alleBestaetigen.ids)}>
                <Icon name="send" className="text-[16px]" /> {laeuft ? "Gibt frei …" : `Ja, alle ${alleBestaetigen.ids.length} freigeben`}
              </Button>
            </>
          }
        >
          <div>
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              {`Die Mails gehen in den nächsten Minuten an ${alleBestaetigen.ids.length} Gäste, die sich für ${alleBestaetigen.titel} vorgemerkt haben. Wer sich bis zum Versand noch abmeldet, bekommt keine Mail.`}
            </p>
          </div>
        </Modal>
      )}

      {zuVerwerfen && (
        <VerwerfenDialog
          // Der Schlüssel setzt das Grund-Feld zurück, sobald ein anderer Ausgang verworfen wird.
          // Ohne ihn stünde der Grund der vorigen Zeile im Feld — und würde womöglich abgeschickt.
          key={zuVerwerfen.id}
          ausgang={zuVerwerfen}
          laeuft={laeuft}
          onAbbrechen={() => setVerwerfenId(null)}
          onVerwerfen={(grund) => void entscheide(zuVerwerfen, "verwerfen", grund)}
        />
      )}
    </>
  );
}
